import { Sites } from '@ps/platform-message-framework/common/Sites';
import { Currency, FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';

export interface ConvertedCurrency {
  conversionRate: number;
  toCurrency: Currency;
  fromCurrency: Currency;
}

export const convertedCurrencyList: {
  [key: string]: ConvertedCurrency;
} = {
  [Sites.BUL]: {
    conversionRate: 1.95583,
    toCurrency: Currency.BulgarianLev,
    fromCurrency: Currency.Euro
  }
};

export const useConvertCurrency = (currency?: Currency, amount?: string | number) => {
  const site = useSelector(select.cms.getSiteId);
  const displayConvertedAmount = useFeature(FeatureFlag.DisplayConvertedAmount);
  const { formatNumber } = useIntl();

  const calculateConvertedValue = useCallback(
    (amount: number | string) => {
      let numberValue = Number(amount);

      if (isNaN(numberValue)) {
        numberValue = 0;
      }

      return formatNumber(numberValue * convertedCurrencyList[site].conversionRate, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        currency: convertedCurrencyList[site].toCurrency,
        style: 'currency'
      });
    },
    [formatNumber, site]
  );

  const convertAmount = useCallback(
    (amount: string | number, selectedCurrency: Currency): string | undefined => {
      const isConverted =
        displayConvertedAmount &&
        site in convertedCurrencyList &&
        selectedCurrency === convertedCurrencyList[site].fromCurrency;

      if (!isConverted) return undefined;

      return calculateConvertedValue(amount);
    },
    [calculateConvertedValue, displayConvertedAmount, site]
  );

  const convertedAmountValue = useMemo(() => {
    if (amount === undefined || !currency) return undefined;

    return convertAmount(amount, currency);
  }, [amount, convertAmount, currency]);

  return { convertedAmountValue, convertAmount };
};

export default useConvertCurrency;
